import React, { useCallback } from 'react';

import { Form, Modal, Space } from 'antd-v5';
import { isNil } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { Input, Text } from 'scouting-ui/lib';

import { organizationGuidSel } from '@shared/duck/selectors';
import { intl } from '@shared/localization';
import { openReportWithQueryParams } from '@utils/openBasicReport';

import { NormalizedReportItem } from '../duck/utils';

interface ByMemberIdModalProps {
  isVisible: boolean;
  report: NormalizedReportItem | null;
  onClose: () => void;
}

interface ByMemberIdForm {
  lastName: string;
  memberId: number;
}

enum FormFields {
  lastName = 'lastName',
  memberId = 'memberId',
}

const ByMemberIdModal: React.FC<ByMemberIdModalProps> = ({
  isVisible,
  report,
  onClose,
}) => {
  const [form] = Form.useForm<ByMemberIdForm>();
  const organizationGuid = useSelector(organizationGuidSel);

  const handleFinish = useCallback(async () => {
    const valuesSubmitted = await form.validateFields();
    if (isNil(report)) return;

    const params = {
      organizationGuid,
      MemberID: `${valuesSubmitted.memberId}`.trim(),
      LastName: valuesSubmitted.lastName.trim(),
    };

    openReportWithQueryParams(report?.ReportURI, params);
  }, [organizationGuid, report, form]);

  return (
    <Modal
      width={800}
      open={isVisible}
      destroyOnClose
      title={<FormattedMessage id="reports.BasicReport.byMemberId.title" />}
      cancelText={<FormattedMessage id="shared.cancel" />}
      okText={<FormattedMessage id="shared.selectMember" />}
      onOk={handleFinish}
      cancelButtonProps={{
        type: 'default',
        onClick: onClose,
      }}
      onCancel={onClose}
    >
      <Form clearOnDestroy form={form} initialValues={{}}>
        <Space direction="vertical">
          <Text strong>
            <FormattedMessage id="reports.BasicReport.byMemberId.description" />
          </Text>
          <Form.Item
            name={FormFields.lastName}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'shared.form.error.isRequired',
                }),
              },
            ]}
            noStyle
          >
            <Input
              placeholder={intl.formatMessage({
                id: 'shared.personalInfo.lastName',
              })}
              size="small"
            />
          </Form.Item>
          <Form.Item
            name={FormFields.memberId}
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  id: 'shared.form.error.isRequired',
                }),
              },
            ]}
            noStyle
          >
            <Input
              placeholder={intl.formatMessage({
                id: 'shared.memberId',
              })}
              size="small"
            />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  );
};

export default ByMemberIdModal;
